import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycPartyCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { CompanyProfileDto } from "@/features/kyc/api/dto";

/**
 * Attache un profil entreprise à une identité de type COMPANY
 * (`POST /CompanyProfiles/add`). Idempotency-Key NON requis (guide §6.2) :
 * l'unicité est garantie par le hash du numéro d'immatriculation et de
 * l'identifiant fiscal côté serveur.
 */
export const useCreateCompanyProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "companyProfiles", "create"],
    mutationFn: (body: CompanyProfileDto) =>
      mutateData({
        url: kycEndpoints.companyProfiles.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<CompanyProfileDto>,
    onSuccess: (_data, body) => {
      return invalidateAfterMutation(
        queryClient,
        kycPartyCacheKeys(body.partyId),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateCompanyProfile failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
